import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Plus } from "lucide-react";
import {
  CAREER_UNIT_KINDS,
  type CareerUnit,
  type CareerUnitKind,
} from "@resume-cloudflare/shared";
import {
  KIND_LABELS,
  kindLabel,
  totalUnits,
  useCorpus,
  useCreateCorpusUnit,
  useDeleteCorpusUnit,
  useImportResume,
  useUpdateCorpusUnit,
  type ByKind,
} from "../lib/corpus";
import { useResumes } from "../lib/resumes";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill } from "../components/ui/Pill";
import { Skeleton, SkeletonText } from "../components/ui/Skeleton";
import { Sheet } from "../components/ui/Sheet";
import { Dialog } from "../components/ui/Dialog";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/Textarea";
import { Select } from "../components/ui/Select";
import {
  Table as UITable,
  TableShell,
  TCell,
  THead,
  TRow,
} from "../components/ui/Table";
import { activateOnKey } from "../lib/keyboard";

type Tab = "roles" | "projects" | "skills" | "other";

const TABS: { id: Tab; label: string }[] = [
  { id: "roles", label: "Roles" },
  { id: "projects", label: "Projects" },
  { id: "skills", label: "Skills" },
  { id: "other", label: "Other" },
];

const TAB_KINDS: Record<Tab, CareerUnitKind[]> = {
  roles: ["role", "bullet"],
  projects: ["project"],
  skills: ["skill"],
  other: ["summary", "education", "cert", "award", "story"],
};

function tabCount(tab: Tab, by_kind: ByKind): number {
  return TAB_KINDS[tab].reduce((n, k) => n + (by_kind[k] ?? 0), 0);
}

export default function Corpus() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: Tab = (TABS.find((t) => t.id === tabParam)?.id ?? "roles") as Tab;
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  const q = useCorpus();
  const [selected, setSelected] = useState<CareerUnit | null>(null);
  const [addKind, setAddKind] = useState<CareerUnitKind | null>(null);

  const units = q.data?.units ?? [];
  const by_kind = q.data?.by_kind ?? {};
  const usedInVersions: Record<string, number> = q.data?.used_in_versions ?? {};
  const isEmpty = !q.isLoading && totalUnits(by_kind) === 0;

  return (
    <>
      <section className="p-4 sm:p-6 lg:p-8 space-y-5">
        <header className="flex items-baseline justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">
              Career corpus
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-neutral-400">
              The atomic units (roles, bullets, projects, skills, summary,
              education, certs) that future tailored resumes draw from. Each
              unit is embedded into the career index so the tailor can pull the
              most relevant material per JD.
            </p>
          </div>
          <span className="text-xs text-neutral-500" aria-live="polite">
            {q.data ? (
              `${totalUnits(by_kind)} units`
            ) : q.isLoading ? (
              <Skeleton className="inline-block h-3 w-16" />
            ) : null}
          </span>
        </header>

        {q.isLoading ? (
          <Card className="p-6">
            <SkeletonText lines={4} />
          </Card>
        ) : q.error ? (
          <Card>
            <EmptyState
              tone="danger"
              title="Couldn't load corpus"
              description={String(q.error)}
              action={
                <Button variant="secondary" onClick={() => q.refetch()}>
                  Retry
                </Button>
              }
            />
          </Card>
        ) : isEmpty ? (
          <ImportBanner />
        ) : (
          <>
            <TabBar tab={tab} setTab={setTab} by_kind={by_kind} />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Plus size={14} />}
                onClick={() => setAddKind(defaultAddKindFor(tab))}
              >
                Add {kindLabel(defaultAddKindFor(tab))}
              </Button>
            </div>

            {tab === "roles" ? (
              <RolesTab
                units={units}
                onSelect={setSelected}
                usedInVersions={usedInVersions}
              />
            ) : tab === "projects" ? (
              <ProjectsTab
                units={units}
                onSelect={setSelected}
                usedInVersions={usedInVersions}
              />
            ) : tab === "skills" ? (
              <SkillsTab
                units={units}
                onSelect={setSelected}
                usedInVersions={usedInVersions}
              />
            ) : (
              <OtherTab units={units} onSelect={setSelected} />
            )}
          </>
        )}
      </section>

      <Sheet
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
      >
        <Sheet.Content side="right" className="md:max-w-xl">
          {selected ? (
            <UnitSidePanel
              key={selected.id}
              unit={selected}
              usedInVersions={usedInVersions[selected.id] ?? 0}
              onSaved={(u) => setSelected(u)}
              onDeleted={() => setSelected(null)}
            />
          ) : null}
        </Sheet.Content>
      </Sheet>

      <Dialog
        open={addKind !== null}
        onOpenChange={(o) => {
          if (!o) setAddKind(null);
        }}
      >
        {addKind ? (
          <AddUnitDialog
            kind={addKind}
            roles={units.filter((u) => u.kind === "role")}
            onClose={() => setAddKind(null)}
          />
        ) : null}
      </Dialog>
    </>
  );
}

function defaultAddKindFor(tab: Tab): CareerUnitKind {
  switch (tab) {
    case "roles":
      return "bullet";
    case "projects":
      return "project";
    case "skills":
      return "skill";
    case "other":
      return "cert";
  }
}

function TabBar({
  tab,
  setTab,
  by_kind,
}: {
  tab: Tab;
  setTab: (t: Tab) => void;
  by_kind: ByKind;
}) {
  return (
    <div
      role="tablist"
      aria-label="Corpus sections"
      className="flex flex-wrap gap-1 border-b border-neutral-800"
    >
      {TABS.map((t) => {
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setTab(t.id)}
            className={
              "relative -mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 " +
              (active
                ? "border-accent-500 text-neutral-50"
                : "border-transparent text-neutral-400 hover:text-neutral-200")
            }
          >
            <span>{t.label}</span>
            <span className="text-[10px] tabular-nums text-neutral-500">
              {tabCount(t.id, by_kind)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Roles tab ────────────────────────────────────────────────────────────

function RolesTab({
  units,
  onSelect,
  usedInVersions,
}: {
  units: CareerUnit[];
  onSelect: (u: CareerUnit) => void;
  usedInVersions: Record<string, number>;
}) {
  const roles = useMemo(
    () =>
      units
        .filter((u) => u.kind === "role")
        .sort((a, b) => compareDateDesc(a.start_date, b.start_date)),
    [units],
  );
  const bulletsByRole = useMemo(() => {
    const map = new Map<string, CareerUnit[]>();
    for (const u of units) {
      if (u.kind !== "bullet") continue;
      const key = u.parent_id ?? "__orphan__";
      const arr = map.get(key) ?? [];
      arr.push(u);
      map.set(key, arr);
    }
    return map;
  }, [units]);
  const orphans = bulletsByRole.get("__orphan__") ?? [];

  if (roles.length === 0 && orphans.length === 0) {
    return (
      <Card>
        <EmptyState title="No roles or bullets yet. Add one to get started." />
      </Card>
    );
  }

  return (
    <ol className="space-y-4">
      {roles.map((role) => {
        const bullets = bulletsByRole.get(role.id) ?? [];
        return (
          <li key={role.id}>
            <Card className="p-5">
              <RoleHeader
                role={role}
                onSelect={() => onSelect(role)}
                used={usedInVersions[role.id] ?? 0}
              />
              {bullets.length > 0 ? (
                <ul className="mt-4 space-y-2 border-l border-neutral-800 pl-4">
                  {bullets.map((b) => (
                    <BulletRow
                      key={b.id}
                      bullet={b}
                      onSelect={() => onSelect(b)}
                      used={usedInVersions[b.id] ?? 0}
                    />
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-neutral-500">
                  No bullets attached to this role yet.
                </p>
              )}
            </Card>
          </li>
        );
      })}

      {orphans.length > 0 ? (
        <li>
          <Card className="p-5">
            <h3 className="text-sm font-medium text-neutral-100">
              Unattached bullets
            </h3>
            <p className="mt-1 text-xs text-neutral-500">
              Bullets without a parent role — link them by editing their
              parent.
            </p>
            <ul className="mt-4 space-y-2 border-l border-neutral-800 pl-4">
              {orphans.map((b) => (
                <BulletRow
                  key={b.id}
                  bullet={b}
                  onSelect={() => onSelect(b)}
                  used={usedInVersions[b.id] ?? 0}
                />
              ))}
            </ul>
          </Card>
        </li>
      ) : null}
    </ol>
  );
}

function RoleHeader({
  role,
  onSelect,
  used,
}: {
  role: CareerUnit;
  onSelect: () => void;
  used: number;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onKeyDown={(e) => activateOnKey(e, onSelect)}
      className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-neutral-50">
          {role.title ?? "Untitled role"}
        </h3>
        <div className="flex items-center gap-2">
          <UsedBadge used={used} />
          <span className="text-xs text-neutral-500">
            {formatDateRange(role.start_date, role.end_date)}
          </span>
        </div>
      </div>
      {role.company ? (
        <p className="mt-0.5 text-sm text-neutral-300">{role.company}</p>
      ) : null}
      {role.content ? (
        <p className="mt-2 text-sm text-neutral-400 line-clamp-3">
          {role.content}
        </p>
      ) : null}
      {role.tags.length > 0 ? (
        <TagRow tags={role.tags} className="mt-3" />
      ) : null}
    </button>
  );
}

function BulletRow({
  bullet,
  onSelect,
  used,
}: {
  bullet: CareerUnit;
  onSelect: () => void;
  used: number;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        onKeyDown={(e) => activateOnKey(e, onSelect)}
        className="block w-full text-left rounded-md px-2 py-1 text-sm text-neutral-300 hover:bg-neutral-900/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-neutral-200">• {bullet.content}</span>
          <UsedBadge used={used} />
        </div>
        {bullet.tags.length > 0 ? (
          <TagRow tags={bullet.tags} className="mt-1.5" />
        ) : null}
      </button>
    </li>
  );
}

// "Used in N resume versions" pill — wave 4 re-introduces this after wave 3
// hid it (resume_versions was empty pre-wave-4). Renders nothing for N=0
// so unused units don't get visual noise.
function UsedBadge({ used }: { used: number }) {
  if (used <= 0) return null;
  return (
    <Pill tone="success" className="shrink-0">
      Used in {used}
    </Pill>
  );
}

// ── Projects tab ─────────────────────────────────────────────────────────

function ProjectsTab({
  units,
  onSelect,
  usedInVersions,
}: {
  units: CareerUnit[];
  onSelect: (u: CareerUnit) => void;
  usedInVersions: Record<string, number>;
}) {
  const projects = units.filter((u) => u.kind === "project");
  if (projects.length === 0) {
    return (
      <Card>
        <EmptyState title="No projects yet. Add one to get started." />
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {projects.map((p) => (
        <ProjectCard
          key={p.id}
          project={p}
          onSelect={() => onSelect(p)}
          used={usedInVersions[p.id] ?? 0}
        />
      ))}
    </div>
  );
}

function ProjectCard({
  project,
  onSelect,
  used,
}: {
  project: CareerUnit;
  onSelect: () => void;
  used: number;
}) {
  return (
    <Card className="flex flex-col p-4">
      <button
        type="button"
        onClick={onSelect}
        onKeyDown={(e) => activateOnKey(e, onSelect)}
        className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-neutral-50 line-clamp-2">
            {project.title ?? "Untitled project"}
          </h3>
          <UsedBadge used={used} />
        </div>
        <p className="mt-2 text-xs text-neutral-400 line-clamp-4">
          {project.content}
        </p>
      </button>
      {project.tags.length > 0 ? (
        <TagRow tags={project.tags} className="mt-3" />
      ) : null}
    </Card>
  );
}

// ── Skills tab ───────────────────────────────────────────────────────────

function SkillsTab({
  units,
  onSelect,
  usedInVersions,
}: {
  units: CareerUnit[];
  onSelect: (u: CareerUnit) => void;
  usedInVersions: Record<string, number>;
}) {
  const skills = useMemo(
    () =>
      units
        .filter((u) => u.kind === "skill")
        .sort((a, b) => a.content.localeCompare(b.content)),
    [units],
  );
  if (skills.length === 0) {
    return (
      <Card>
        <EmptyState title="No skills yet. Add one to get started." />
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="text-xs uppercase tracking-wider text-neutral-500">
          Cloud
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {skills.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect(s)}
              className="inline-flex items-center rounded-full border border-neutral-700 bg-neutral-800/60 px-2.5 py-0.5 text-xs text-neutral-200 hover:border-accent-500 hover:text-neutral-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
            >
              {s.content}
            </button>
          ))}
        </div>
      </Card>
      <TableShell>
        <UITable>
          <THead>
            <tr>
              <th scope="col" className="px-4 py-3 text-left font-medium">
                Skill
              </th>
              <th scope="col" className="px-4 py-3 text-left font-medium">
                Tags
              </th>
              <th scope="col" className="px-4 py-3 text-left font-medium">
                Used
              </th>
              <th scope="col" className="px-4 py-3 text-left font-medium">
                Source
              </th>
              <th scope="col" className="px-4 py-3 text-left font-medium">
                Added
              </th>
            </tr>
          </THead>
          <tbody>
            {skills.map((s) => (
              <TRow
                key={s.id}
                tabIndex={0}
                role="button"
                onClick={() => onSelect(s)}
                onKeyDown={(e) => activateOnKey(e, () => onSelect(s))}
                className="cursor-pointer hover:bg-neutral-900/60"
              >
                <TCell className="font-medium text-neutral-100">
                  {s.content}
                </TCell>
                <TCell>
                  {s.tags.length > 0 ? (
                    <TagRow tags={s.tags} />
                  ) : (
                    <span className="text-neutral-600">—</span>
                  )}
                </TCell>
                <TCell>
                  {usedInVersions[s.id] ? (
                    <UsedBadge used={usedInVersions[s.id]!} />
                  ) : (
                    <span className="text-neutral-600">—</span>
                  )}
                </TCell>
                <TCell className="text-neutral-500">
                  {sourceLabel(s.source)}
                </TCell>
                <TCell className="text-neutral-500">
                  {ageLabel(s.created_at)}
                </TCell>
              </TRow>
            ))}
          </tbody>
        </UITable>
      </TableShell>
    </div>
  );
}

// ── Other tab (summary + education + certs) ──────────────────────────────

function OtherTab({
  units,
  onSelect,
}: {
  units: CareerUnit[];
  onSelect: (u: CareerUnit) => void;
}) {
  const summary = units.find((u) => u.kind === "summary");
  const education = units.filter((u) => u.kind === "education");
  const certs = units
    .filter((u) => u.kind === "cert")
    .sort((a, b) => compareDateDesc(a.end_date ?? a.start_date, b.end_date ?? b.start_date));
  const awards = units.filter((u) => u.kind === "award");
  const stories = units.filter((u) => u.kind === "story");

  if (
    !summary &&
    education.length === 0 &&
    certs.length === 0 &&
    awards.length === 0 &&
    stories.length === 0
  ) {
    return (
      <Card>
        <EmptyState title="No summary, education, certifications, recognition, or stories yet." />
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {summary ? (
        <section>
          <SectionHeader title="Summary" />
          <Card className="mt-2 p-5">
            <button
              type="button"
              onClick={() => onSelect(summary)}
              onKeyDown={(e) => activateOnKey(e, () => onSelect(summary))}
              className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
            >
              <p className="text-sm text-neutral-200 whitespace-pre-wrap">
                {summary.content}
              </p>
              {summary.tags.length > 0 ? (
                <TagRow tags={summary.tags} className="mt-3" />
              ) : null}
            </button>
          </Card>
        </section>
      ) : null}

      {education.length > 0 ? (
        <section>
          <SectionHeader title={`Education (${education.length})`} />
          <ul className="mt-2 space-y-2">
            {education.map((e) => (
              <li key={e.id}>
                <Card className="p-4">
                  <button
                    type="button"
                    onClick={() => onSelect(e)}
                    onKeyDown={(ev) => activateOnKey(ev, () => onSelect(e))}
                    className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-sm font-semibold text-neutral-50">
                        {e.title ?? "Education"}
                      </h3>
                      <span className="text-xs text-neutral-500">
                        {formatDateRange(e.start_date, e.end_date)}
                      </span>
                    </div>
                    {e.company ? (
                      <p className="mt-0.5 text-sm text-neutral-300">
                        {e.company}
                      </p>
                    ) : null}
                    {e.content ? (
                      <p className="mt-2 text-sm text-neutral-400">
                        {e.content}
                      </p>
                    ) : null}
                  </button>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {certs.length > 0 ? (
        <section>
          <SectionHeader title={`Certifications (${certs.length})`} />
          <ul className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            {certs.map((c) => (
              <li key={c.id}>
                <Card className="p-3">
                  <button
                    type="button"
                    onClick={() => onSelect(c)}
                    onKeyDown={(ev) => activateOnKey(ev, () => onSelect(c))}
                    className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="text-sm font-medium text-neutral-100 line-clamp-1">
                        {c.title ?? c.content}
                      </h3>
                      {c.end_date || c.start_date ? (
                        <span className="text-[11px] text-neutral-500 tabular-nums">
                          {c.end_date ?? c.start_date}
                        </span>
                      ) : null}
                    </div>
                    {c.company ? (
                      <p className="mt-0.5 text-xs text-neutral-400">
                        {c.company}
                      </p>
                    ) : null}
                  </button>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {awards.length > 0 ? (
        <section>
          <SectionHeader title={`Recognition (${awards.length})`} />
          <ul className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            {awards.map((a) => (
              <li key={a.id}>
                <Card className="p-3">
                  <button
                    type="button"
                    onClick={() => onSelect(a)}
                    onKeyDown={(ev) => activateOnKey(ev, () => onSelect(a))}
                    className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="text-sm font-medium text-neutral-100 line-clamp-1">
                        {a.title ?? a.content}
                      </h3>
                      {a.end_date || a.start_date ? (
                        <span className="text-[11px] text-neutral-500 tabular-nums">
                          {a.end_date ?? a.start_date}
                        </span>
                      ) : null}
                    </div>
                    {a.company ? (
                      <p className="mt-0.5 text-xs text-neutral-400">
                        {a.company}
                      </p>
                    ) : null}
                  </button>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {stories.length > 0 ? (
        <section>
          <SectionHeader title={`Stories (${stories.length})`} />
          <ul className="mt-2 space-y-2">
            {stories.map((st) => (
              <li key={st.id}>
                <Card className="p-4">
                  <button
                    type="button"
                    onClick={() => onSelect(st)}
                    onKeyDown={(ev) => activateOnKey(ev, () => onSelect(st))}
                    className="block w-full text-left rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-sm font-semibold text-neutral-50">
                        {st.title ?? "Story"}
                      </h3>
                      {st.company ? (
                        <span className="text-xs text-neutral-500">{st.company}</span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm text-neutral-400 line-clamp-3">{st.content}</p>
                  </button>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="text-xs font-medium uppercase tracking-wider text-neutral-500">
      {title}
    </h2>
  );
}

// ── Side panel (view + edit + delete) ────────────────────────────────────

function UnitSidePanel({
  unit,
  usedInVersions,
  onSaved,
  onDeleted,
}: {
  unit: CareerUnit;
  usedInVersions: number;
  onSaved: (u: CareerUnit) => void;
  onDeleted: () => void;
}) {
  const update = useUpdateCorpusUnit();
  const del = useDeleteCorpusUnit();
  const [editing, setEditing] = useState(false);

  const [content, setContent] = useState(unit.content);
  const [title, setTitle] = useState(unit.title ?? "");
  const [company, setCompany] = useState(unit.company ?? "");
  const [startDate, setStartDate] = useState(unit.start_date ?? "");
  const [endDate, setEndDate] = useState(unit.end_date ?? "");
  const [tagsInput, setTagsInput] = useState(unit.tags.join(", "));

  const dirty =
    editing &&
    (content !== unit.content ||
      (title || "") !== (unit.title ?? "") ||
      (company || "") !== (unit.company ?? "") ||
      (startDate || "") !== (unit.start_date ?? "") ||
      (endDate || "") !== (unit.end_date ?? "") ||
      tagsInput !== unit.tags.join(", "));

  function reset() {
    setContent(unit.content);
    setTitle(unit.title ?? "");
    setCompany(unit.company ?? "");
    setStartDate(unit.start_date ?? "");
    setEndDate(unit.end_date ?? "");
    setTagsInput(unit.tags.join(", "));
  }

  async function save() {
    if (!dirty) {
      setEditing(false);
      return;
    }
    const patch: Record<string, unknown> = {};
    if (content !== unit.content) patch.content = content;
    if ((title || null) !== (unit.title ?? null)) patch.title = title || null;
    if ((company || null) !== (unit.company ?? null))
      patch.company = company || null;
    if ((startDate || null) !== (unit.start_date ?? null))
      patch.start_date = startDate || null;
    if ((endDate || null) !== (unit.end_date ?? null))
      patch.end_date = endDate || null;
    const nextTags = parseTags(tagsInput);
    if (!arrayEq(nextTags, unit.tags)) patch.tags = nextTags;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    const res = await update.mutateAsync({
      id: unit.id,
      patch: patch as Parameters<typeof update.mutateAsync>[0]["patch"],
    });
    onSaved(res.unit);
    setEditing(false);
  }

  async function remove() {
    if (!confirm(`Delete this ${kindLabel(unit.kind)}?`)) return;
    await del.mutateAsync(unit.id);
    onDeleted();
  }

  const showDates = unit.kind !== "skill" && unit.kind !== "summary";
  const showCompany =
    unit.kind === "role" ||
    unit.kind === "education" ||
    unit.kind === "cert" ||
    unit.kind === "award" ||
    unit.kind === "story";
  const showTitle =
    unit.kind === "role" ||
    unit.kind === "project" ||
    unit.kind === "education" ||
    unit.kind === "cert" ||
    unit.kind === "award" ||
    unit.kind === "story";

  return (
    <>
      <Sheet.Header
        eyebrow={
          <span className="inline-flex items-center gap-2 flex-wrap">
            <Pill tone="neutral" className="capitalize">
              {KIND_LABELS[unit.kind]}
            </Pill>
            <span className="text-[11px] text-neutral-500">
              {sourceLabel(unit.source)}
            </span>
            {unit.embedded_at ? (
              <Pill tone="success">embedded</Pill>
            ) : (
              <Pill tone="warning">embedding…</Pill>
            )}
            {usedInVersions > 0 ? (
              <Pill tone="success">
                Used in {usedInVersions} resume version
                {usedInVersions === 1 ? "" : "s"}
              </Pill>
            ) : null}
          </span>
        }
        title={unit.title ?? unit.content.slice(0, 64)}
      />
      <Sheet.Body className="space-y-4">
        {editing ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            {showTitle ? (
              <Field label="Title">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    unit.kind === "cert" ? "Certification name" : "Title"
                  }
                />
              </Field>
            ) : null}
            {showCompany ? (
              <Field
                label={
                  unit.kind === "education"
                    ? "School"
                    : unit.kind === "cert"
                      ? "Issuer"
                      : "Company"
                }
              >
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </Field>
            ) : null}
            {showDates ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start">
                  <Input
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="2020-01"
                  />
                </Field>
                <Field label="End">
                  <Input
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="present"
                  />
                </Field>
              </div>
            ) : null}
            <Field label="Content">
              <Textarea
                rows={unit.kind === "summary" ? 8 : 5}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </Field>
            <Field
              label="Tags"
              hint="Comma-separated. Re-tagging happens automatically when content changes."
            >
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="bgp, sd-wan, networking"
              />
            </Field>
          </form>
        ) : (
          <>
            <dl className="grid grid-cols-1 gap-2 text-sm">
              {showTitle && unit.title ? (
                <Row label="Title">{unit.title}</Row>
              ) : null}
              {showCompany && unit.company ? (
                <Row
                  label={
                    unit.kind === "education"
                      ? "School"
                      : unit.kind === "cert"
                        ? "Issuer"
                        : "Company"
                  }
                >
                  {unit.company}
                </Row>
              ) : null}
              {showDates && (unit.start_date || unit.end_date) ? (
                <Row label="Dates">
                  {formatDateRange(unit.start_date, unit.end_date)}
                </Row>
              ) : null}
            </dl>
            <div>
              <p className="text-xs uppercase tracking-wider text-neutral-500">
                Content
              </p>
              <p className="mt-2 text-sm text-neutral-200 whitespace-pre-wrap">
                {unit.content}
              </p>
            </div>
            {unit.tags.length > 0 ? (
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-500">
                  Tags
                </p>
                <TagRow tags={unit.tags} className="mt-2" />
              </div>
            ) : null}
          </>
        )}
      </Sheet.Body>
      <Sheet.Footer className="justify-between">
        <Button
          variant="danger"
          size="sm"
          onClick={remove}
          loading={del.isPending}
        >
          Delete
        </Button>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                reset();
                setEditing(false);
              }}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={!dirty}
              loading={update.isPending}
            >
              Save
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
      </Sheet.Footer>
    </>
  );
}

// ── Add unit dialog ──────────────────────────────────────────────────────

function AddUnitDialog({
  kind: initialKind,
  roles,
  onClose,
}: {
  kind: CareerUnitKind;
  roles: CareerUnit[];
  onClose: () => void;
}) {
  const create = useCreateCorpusUnit();
  const [kind, setKind] = useState<CareerUnitKind>(initialKind);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [parentId, setParentId] = useState<string>("");
  const [tagsInput, setTagsInput] = useState("");

  const showTitle =
    kind === "role" ||
    kind === "project" ||
    kind === "education" ||
    kind === "cert" ||
    kind === "award" ||
    kind === "story";
  const showCompany =
    kind === "role" ||
    kind === "education" ||
    kind === "cert" ||
    kind === "award" ||
    kind === "story";
  const showDates = kind !== "skill" && kind !== "summary";
  const showParent = kind === "bullet" || kind === "story";

  const canSubmit = content.trim().length > 0 && !create.isPending;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const tags = parseTags(tagsInput);
    await create.mutateAsync({
      kind,
      title: title.trim() || null,
      content: content.trim(),
      company: company.trim() || null,
      start_date: startDate.trim() || null,
      end_date: endDate.trim() || null,
      parent_id: parentId || null,
      tags: tags.length > 0 ? tags : undefined,
    });
    onClose();
  }

  return (
    <Dialog.Content
      title={`Add ${kindLabel(kind)}`}
      description="Saved units are embedded into the career index in the background."
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={!canSubmit}
            loading={create.isPending}
          >
            Add
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <Field label="Kind">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as CareerUnitKind)}
          >
            {CAREER_UNIT_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {showParent ? (
          <Field label="Attach to role" hint="Optional — leave empty for an unattached bullet.">
            <Select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">— none —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title ?? "Untitled role"}
                  {r.company ? ` @ ${r.company}` : ""}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {showTitle ? (
          <Field label="Title">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                kind === "cert"
                  ? "Certification name"
                  : kind === "role"
                    ? "Senior Pre-sales Engineer"
                    : "Title"
              }
            />
          </Field>
        ) : null}
        {showCompany ? (
          <Field
            label={
              kind === "education"
                ? "School"
                : kind === "cert"
                  ? "Issuer"
                  : "Company"
            }
          >
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </Field>
        ) : null}
        {showDates ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start">
              <Input
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="2020-01"
              />
            </Field>
            <Field label="End">
              <Input
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="present"
              />
            </Field>
          </div>
        ) : null}
        <Field label="Content" hint={kind === "skill" ? "The skill name." : undefined}>
          <Textarea
            rows={kind === "summary" ? 8 : kind === "skill" ? 1 : 5}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={contentPlaceholder(kind)}
          />
        </Field>
        <Field label="Tags" hint="Comma-separated. Auto-tagged after embedding if left blank.">
          <Input
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="bgp, sd-wan"
          />
        </Field>
      </form>
    </Dialog.Content>
  );
}

function contentPlaceholder(kind: CareerUnitKind): string {
  switch (kind) {
    case "bullet":
      return "Drove a 35% reduction in mean time to resolution by …";
    case "role":
      return "What you did at this role overall — supplements bullets.";
    case "project":
      return "What the project does, what your role was, what shipped.";
    case "skill":
      return "kubernetes";
    case "summary":
      return "A two-to-four sentence executive summary.";
    case "education":
      return "Degree, focus, honors, relevant coursework.";
    case "cert":
      return "Optional context — what the cert covers.";
    case "award":
      return "The recognition, plus any brief context.";
    case "story":
      return "Situation, what you did, and the result — the depth behind a resume item.";
  }
}

// ── Empty-state import banner ───────────────────────────────────────────

function ImportBanner() {
  const resumesQ = useResumes();
  const importMut = useImportResume();
  const [resumeId, setResumeId] = useState("");

  const resumes = resumesQ.data?.resumes ?? [];

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-neutral-50">
          Import from a resume
        </h2>
        <p className="mt-1 text-sm text-neutral-400">
          Pick one of your uploaded resumes and we'll extract roles, bullets,
          projects, skills, education, certifications, and recognition into
          your corpus.
          Each unit is embedded into the career index in the background.
        </p>
      </div>
      {resumesQ.isLoading ? (
        <SkeletonText lines={2} />
      ) : resumes.length === 0 ? (
        <div className="rounded-md border border-neutral-800 bg-neutral-900/40 p-4 text-sm text-neutral-400">
          No resumes uploaded yet — head to{" "}
          <a className="text-accent-300 hover:text-accent-200" href="/resumes">
            /resumes
          </a>{" "}
          to upload one first.
        </div>
      ) : (
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!resumeId) return;
            await importMut.mutateAsync(resumeId);
          }}
        >
          <label className="block flex-1">
            <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
              Resume
            </span>
            <Select
              className="mt-1"
              value={resumeId}
              onChange={(e) => setResumeId(e.target.value)}
            >
              <option value="">— pick one —</option>
              {resumes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </Select>
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!resumeId || importMut.isPending}
            loading={importMut.isPending}
          >
            Import
          </Button>
        </form>
      )}
    </Card>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
      {hint ? (
        <span className="mt-1 block text-[11px] text-neutral-500">{hint}</span>
      ) : null}
    </label>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-3">
      <dt className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </dt>
      <dd className="text-neutral-200">{children}</dd>
    </div>
  );
}

function TagRow({ tags, className }: { tags: string[]; className?: string }) {
  return (
    <div className={"flex flex-wrap gap-1 " + (className ?? "")}>
      {tags.map((t) => (
        <Pill key={t} tone="neutral">
          {t}
        </Pill>
      ))}
    </div>
  );
}

function parseTags(s: string): string[] {
  return s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function arrayEq(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function compareDateDesc(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  // Treat null as oldest so present-day rows sort first.
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return b.localeCompare(a);
}

function formatDateRange(
  start: string | null,
  end: string | null,
): string {
  if (!start && !end) return "";
  if (start && end) return `${start} – ${end}`;
  return start ?? end ?? "";
}

function sourceLabel(source: string): string {
  if (source === "manual") return "manual";
  if (source.startsWith("imported_from_resume:")) return "from resume";
  return source;
}

function ageLabel(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86400_000);
  if (days <= 0) {
    const h = Math.floor((Date.now() - ts) / 3600_000);
    if (h <= 0) return "just now";
    return h === 1 ? "1 hour ago" : `${h} hours ago`;
  }
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}
